import {
	Box,
	Button,
	CircularProgress,
	Paper,
	TextField,
	Typography,
	FormControlLabel,
	Radio,
	RadioGroup,
	FormLabel,
	Grid,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import MDButton from "../../components/MDButton";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import apiService from "../../services/api-service";

const NonConformitaChiusura = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const sigCanvas = useRef(null);

	const [nc, setNc] = useState(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	const [formData, setFormData] = useState({
		esito_positivo: true,
		tempo_rilavorazione: "",
		materiale_scartato: "",
		costo: "",
		azione_correttiva: "",
	});

	const [fieldErrors, setFieldErrors] = useState({
		tempo_rilavorazione: false,
		costo: false,
		azione_correttiva: false,
		firma: false,
	});

	useEffect(() => {
		fetchData();
	}, [id]);

	const fetchData = async () => {
		setLoading(true);
		try {
			// Fetch NC details
			const ncResponse = await apiService.get(`/non-conformita/${id}/`, {}, {});

			if (ncResponse.stato !== "IN_SOLUZIONE") {
				toast.error(
					`NC deve essere in stato IN_SOLUZIONE (stato attuale: ${ncResponse.stato_display})`
				);
				navigate("/quality/non-conformita");
				return;
			}

			setNc(ncResponse);
		} catch (error) {
			toast.error("Errore nel caricamento dati");
			navigate("/quality/non-conformita");
		} finally {
			setLoading(false);
		}
	};

	const handleInputChange = (name, value) => {
		setFormData((prev) => ({ ...prev, [name]: value }));
		setFieldErrors((prev) => ({ ...prev, [name]: false }));
	};

	const clearSignature = () => {
		if (sigCanvas.current) {
			sigCanvas.current.clear();
			setFieldErrors((prev) => ({ ...prev, firma: false }));
		}
	};

	const handleSave = async () => {
		// Validazione
		const errors = {
			tempo_rilavorazione:
				!formData.tempo_rilavorazione || parseFloat(formData.tempo_rilavorazione) < 0,
			costo: !formData.costo || parseFloat(formData.costo) < 0,
			azione_correttiva: !formData.esito_positivo && !formData.azione_correttiva,
			firma: !sigCanvas.current || sigCanvas.current.isEmpty(),
		};

		setFieldErrors(errors);

		if (Object.values(errors).some((err) => err)) {
			toast.error("Compilare tutti i campi obbligatori");
			return;
		}

		setSaving(true);

		try {
			const firmaBase64 = sigCanvas.current.toDataURL();

			const payload = {
				esito_positivo: formData.esito_positivo,
				tempo_rilavorazione: parseFloat(formData.tempo_rilavorazione),
				costo: parseFloat(formData.costo),
				materiale_scartato: formData.materiale_scartato,
				firma_chiusura: firmaBase64,
			};

			// Aggiungi azione correttiva solo se esito negativo
			if (!formData.esito_positivo && formData.azione_correttiva) {
				payload.azione_correttiva = formData.azione_correttiva;
			}

			await apiService.post(`/non-conformita/${id}/chiudi/`, {}, payload);

			const esitoMsg = formData.esito_positivo ? "positivamente" : "negativamente";
			toast.success(`NC chiusa ${esitoMsg} con successo`);

			setTimeout(() => {
				navigate("/quality/non-conformita");
			}, 1500);
		} catch (error) {
			console.error("Errore chiusura NC:", error);
			toast.error(error.response?.data?.error || "Errore durante la chiusura");
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return (
			<DashboardLayout>
				<DashboardNavbar />
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
					<CircularProgress />
				</Box>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Box p={3}>
				<Paper elevation={3} sx={{ p: 4, maxWidth: 900, margin: "0 auto" }}>
					<Typography variant="h4" gutterBottom color="primary">
						Chiusura NC {nc?.numero}
					</Typography>

					<Box mt={2} mb={3}>
						<Typography variant="body2" color="textSecondary">
							Cantiere: <strong>{nc?.cantiere_nome}</strong>
						</Typography>
						<Typography variant="body2" color="textSecondary">
							Descrizione NC: <strong>{nc?.descrizione}</strong>
						</Typography>
						{nc?.soluzione && (
							<>
								<Typography variant="body2" color="textSecondary" mt={1}>
									Soluzione: <strong>{nc.soluzione.tipo_soluzione_display}</strong>
								</Typography>
								<Typography variant="body2" color="textSecondary">
									Assegnato a: <strong>{nc.soluzione.assegnato_a_nome}</strong>
								</Typography>
							</>
						)}
					</Box>

					<Box mt={3} display="flex" flexDirection="column" gap={3}>
						{/* Esito */}
						<Box>
							<FormLabel component="legend">Esito Controllo Finale *</FormLabel>
							<RadioGroup
								row
								value={formData.esito_positivo}
								onChange={(e) =>
									handleInputChange("esito_positivo", e.target.value === "true")
								}
							>
								<FormControlLabel
									value={true}
									control={<Radio />}
									label="Esito Positivo"
								/>
								<FormControlLabel
									value={false}
									control={<Radio />}
									label="Esito Negativo"
								/>
							</RadioGroup>
						</Box>

						<Grid container spacing={2}>
							{/* Tempo Rilavorazione */}
							<Grid item xs={6}>
								<TextField
									label="Tempo Rilavorazione (ore) *"
									type="number"
									value={formData.tempo_rilavorazione}
									onChange={(e) =>
										handleInputChange("tempo_rilavorazione", e.target.value)
									}
									error={fieldErrors.tempo_rilavorazione}
									helperText={
										fieldErrors.tempo_rilavorazione
											? "Campo obbligatorio (≥0)"
											: "Ore totali impiegate per la rilavorazione"
									}
									inputProps={{ min: 0, step: 0.5 }}
									fullWidth
								/>
							</Grid>

							{/* Costo */}
							<Grid item xs={6}>
								<TextField
									label="Costo Totale (€) *"
									type="number"
									value={formData.costo}
									onChange={(e) => handleInputChange("costo", e.target.value)}
									error={fieldErrors.costo}
									helperText={
										fieldErrors.costo
											? "Campo obbligatorio (≥0)"
											: "Costo complessivo della non conformità"
									}
									inputProps={{ min: 0, step: 0.01 }}
									fullWidth
								/>
							</Grid>
						</Grid>

						{/* Materiale Scartato */}
						<TextField
							label="Materiale Scartato"
							value={formData.materiale_scartato}
							onChange={(e) => handleInputChange("materiale_scartato", e.target.value)}
							helperText="Descrizione materiale eventualmente scartato (opzionale)"
							fullWidth
						/>

						{/* Azione Correttiva - Solo se esito negativo */}
						{!formData.esito_positivo && (
							<TextField
								label="Azione Correttiva *"
								multiline
								rows={4}
								value={formData.azione_correttiva}
								onChange={(e) => handleInputChange("azione_correttiva", e.target.value)}
								error={fieldErrors.azione_correttiva}
								helperText={
									fieldErrors.azione_correttiva
										? "Campo obbligatorio per esito negativo"
										: "Descrizione azione correttiva da intraprendere"
								}
								fullWidth
							/>
						)}

						{/* Firma Digitale */}
						<Box>
							<Typography variant="subtitle1" gutterBottom>
								Firma Digitale *
							</Typography>
							<Box
								border={fieldErrors.firma ? "2px solid red" : "1px solid #ccc"}
								borderRadius={2}
								p={1}
								bgcolor="#f9f9f9"
							>
								<SignatureCanvas
									ref={sigCanvas}
									canvasProps={{
										width: 800,
										height: 200,
										className: "signature-canvas",
										style: { border: "1px dashed #999" },
									}}
								/>
							</Box>
							<Box mt={1} display="flex" gap={2}>
								<Button variant="outlined" size="small" onClick={clearSignature}>
									Cancella Firma
								</Button>
								{fieldErrors.firma && (
									<Typography variant="caption" color="error">
										Firma obbligatoria
									</Typography>
								)}
							</Box>
						</Box>

						{/* Actions */}
						<Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
							<Button
								variant="outlined"
								onClick={() => navigate("/quality/non-conformita")}
								disabled={saving}
							>
								Annulla
							</Button>
							<MDButton
								variant="gradient"
								color={formData.esito_positivo ? "success" : "error"}
								onClick={handleSave}
								disabled={saving}
							>
								{saving ? (
									<CircularProgress size={24} />
								) : formData.esito_positivo ? (
									"Chiudi Positivamente"
								) : (
									"Chiudi Negativamente"
								)}
							</MDButton>
						</Box>
					</Box>
				</Paper>
			</Box>
		</DashboardLayout>
	);
};

export default NonConformitaChiusura;
